import React, { useState, useEffect, useLayoutEffect } from "react";
import { useFormik } from "formik";
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal } from "./Actions";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { HeaderButton } from "components";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import { Buttons } from "components";
import { ACTIONS } from "consts/Actions";
import { DivisionTitles as titles } from "consts/titulos-de-tablas";
import styles from "styles/pages/Pages.module.scss";
import filtroStyles from "./index.module.scss";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import Filters from "components/Filters";
import { RNHDivisionGet } from "services/services-mia/programa-salud/estructuras/RNHDivisionGet";
import { etiquetaSiNo } from "consts/banderas";
import { RNHDepartamentoGet } from "services/services-mia/programa-salud/estructuras/RNHDepartamentoGet";

export default function Divisiones() {
    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [loading, setLoading] = useState(false);
    const [divisiones, setDivisiones] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);
    const [loadingDptos, setLoadingDptos] = useState(false);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    // Filtro por departamento (client-side): se aplica al enviar el form de Filtros.
    const [filtroDpto, setFiltroDpto] = useState("");
    const { service } = useService();

    const filtros = useFormik({
        initialValues: { departamentoId: "" },
        onSubmit: (values) => {
            setFiltroDpto(values.departamentoId);
            getDivision(values.departamentoId);
        },
    });

    const limpiarFiltros = () => {
        filtros.setFieldValue("departamentoId", "");
        setFiltroDpto("");
        getDivision("");
    };

    // El filtro por departamento va EN LA REQUEST (el back devuelve lo que corresponde),
    // no client-side. Sin depto → trae todas.
    const getDivision = async (departamentoId = filtroDpto) => {
        setDivisiones([]);
        setRowSelected({ status: false, row: {} });
        setLoading(true);
        const { data: result } = await RNHDivisionGet(credentials, { departamentoId: departamentoId || null });
        const newRows = result[0]?.rnhDivisionId ? result?.map((data) => {
            return {
                id: data.rnhDivisionId,
                cod: data.rnhDivisionCod,
                name: data.rnhDivisionNombre,
                desc: data.rnhDivisionDescripcion,
                // `||` y no `??`: el back manda tanto null como "" cuando no hay, y
                // la celda vacia se lee como "falta el dato" en vez de "no tiene".
                dpto: data.rnhDepartamentoNombre || "Sin asignar",
                dptoId: data.rnhDepartamentoId,

                // Los dos permisos, en sus dos formas: la ETIQUETA para la celda y el CRUDO
                // "Y"/"N" que precarga el modal de edicion. Mismo criterio que `status` y
                // `statusCod` en el detalle del agente.
                rotativos: etiquetaSiNo(data.rnhDivisionAdmiteRotativos),
                guardias: etiquetaSiNo(data.rnhDivisionAdmiteGuardias),
                admiteRotativos: data.rnhDivisionAdmiteRotativos,
                admiteGuardias: data.rnhDivisionAdmiteGuardias,

              // No son columnas: viajan en la fila para que Modificar y Eliminar no
              // tengan que volver a pedir el registro.
              version: data.rnhDivisionVersion,
            };
            
        }) : []
        setDivisiones(newRows);
        setLoading(false);
    }
    // Catálogo de departamentos: se trae una sola vez acá y se pasa como prop
    // al insert y al update (evita repetir el request en cada modal).
    const getDepartamentos = async () => {
        setLoadingDptos(true);
        const { data: result } = await RNHDepartamentoGet(credentials);
        const rows = result[0]?.rnhDepartamentoId
            ? result.map((d) => ({
                id: d.rnhDepartamentoId,
                cod: d.rnhDepartamentoCod,
                name: d.rnhDepartamentoNombre,
                desc: d.rnhDepartamentoDescripcion,
            }))
            : [];
        setDepartamentos(rows);
        setLoadingDptos(false);
    }
    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };
    const handleClick = ({ procede, title }) => {
        setModalDetails({ procede, title, isOpen: true });
    };

    useEffect(() => {
        getDivision()
        getDepartamentos()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useLayoutEffect(() => {
        setRoute({
            wordKey: `División - Programa Salud RN`
        })
    }, [service.servicioNombre, setRoute])

    return (
        <main className={styles.main}>
            <Modal size={modalDetails.procede === ACTIONS.DELETE ? 'sm' : 'md'} open={modalDetails.isOpen} title={modalDetails.title} onClose={closeModal}>
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        row={rowSelected.row}
                        updateList={getDivision}
                        closeModal={closeModal}
                        departamentos={departamentos}
                        loadingDptos={loadingDptos}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        row={rowSelected.row}
                        servicio={service}
                        updateList={getDivision}
                        closeModal={closeModal}
                        setterRowSelected={setRowSelected}
                    />
                )}
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        servicio={service}
                        updateList={getDivision}
                        closeModal={closeModal}
                        departamentos={departamentos}
                        loadingDptos={loadingDptos}
                    />
                )}
            </Modal>
            <div className={styles["filters-actions"]}>
                <div className={filtroStyles.filtroAncho} style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={filtros}
                        inputsRender={[
                            {
                                type: "select",
                                name: "departamentoId",
                                label: "Departamento",
                                disabled: loadingDptos,
                                options: departamentos,
                                firstOptionKey: "id",
                                firstOptionValue: "id",
                                firstOptionName: "name",
                                all: true,
                            },
                        ]}
                        disabled={loading || loadingDptos}
                    />
                    <HeaderButton text={"Borrar filtros"} onClick={limpiarFiltros} disabled={loading || loadingDptos}>
                        <FilterAltOffRoundedIcon />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={() => { getDivision() }}
                        disabled={rowSelected.row.id || false}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Nueva División"}
                        handleClick={handleClick}
                        data={{
                            title: "Nueva División",
                            procede: ACTIONS.INSERT
                        }}
                        disabled={rowSelected.row.id || service.servicioPermiso !== 'E'}
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text={"Modificar División"}
                        handleClick={handleClick}
                        data={{
                            title: "Modificar División",
                            procede: ACTIONS.UPDATE
                        }}
                        disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
                    >
                        <EditOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Eliminar División"}
                        handleClick={handleClick}
                        data={{
                            title: "Eliminar División",
                            procede: ACTIONS.DELETE
                        }}
                        disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
                    >
                        <DeleteOutlineRoundedIcon />
                    </HeaderButton>
                </Buttons>
            </div>
            <div className={styles.container}>
                <XGrid
                    rows={divisiones}
                    titles={titles}
                    loading={loading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}